import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  message,
  Popconfirm,
  Table,
  Tooltip,
  Drawer,
  Switch,
} from "antd";
import { isScreenMobile } from "Utils/";
import { createComponentStore, useStoreState } from "easy-peasy";
import React, { useEffect, useState } from "react";
import {
  ParameterStoreModel,
  ACTION_TYPES,
} from "store/models/parameters/general-parameter";
import { useForm } from "react-hook-form";
import Yup from "ValidationModels/localizedYup";
import { yupResolver } from "@hookform/resolvers";
import { clean } from "Utils/";
import { DISPLAY_MODE, RECORD_STATE } from "Constants";
import StateTag from "Components/state-tag";
import { formatDate } from "Utils";
import EditableCell from "./editable-cell";
import ArchiveSwitch from "Components/archive-switch";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import NewParameterButton from "./actions/new";
import DeleteParameterButton from "./actions/delete";
import EditParameterButton from "./actions/edit";
import moment from "moment";

const FormValidation = Yup.object({
  description: Yup.string().required(),
});

export default function ParameterList({
  title,
  api,
  authSubject,
  editableCode,
  ItemsComponent,
  mode = DISPLAY_MODE.EDIT,
  columnsExtended = [],
  ...props
}) {
  const { abilities } = useStoreState((store) => store.auth);
  const IsAuthorized = AuthCheck(abilities, AUTH_ACTIONS.READ, authSubject);

  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerVisibility, setDrawerVisibility] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);

  const isEditing = (record) => record.id === selectedParameter?.id;

  const useParameterStore = createComponentStore(ParameterStoreModel(api));
  const [store, actions] = useParameterStore();

  const { fetch, update, create, remove, fetchDeletedRecords } = actions;

  useEffect(() => {
    if (store.showArchived) fetchDeletedRecords();
    else {
      if (IsAuthorized) fetch();
    }
  }, [store.showArchived]);

  useEffect(() => {
    if (store.process === ACTION_TYPES.FETCHED_DATA) {
      setTableData(store.data);
    }

    if (store.process === ACTION_TYPES.CREATED_DATA) {
      message.success(`Yeni ${title} oluşturuldu.`);
      fetch();
    }

    if (store.process === ACTION_TYPES.UPDATED_DATA) {
      message.success(`${title} güncellendi.`);
      fetch();
    }

    if (store.process === ACTION_TYPES.REMOVED_DATA) {
      message.success(`${title} silindi.`);
      fetch();
    }

    if (store.process === ACTION_TYPES.RESTORED_DATA) {
      message.success(`${title} arşivden geri alındı.`);
      fetch();
    }
  }, [store.process]);

  const columns = [
    {
      title: "Kod",
      dataIndex: "code",
      key: "code",
      width: isScreenMobile() ? null : 100,
      editable: editableCode || false,
    },
    {
      title: "Ad",
      dataIndex: "description",
      key: "description",
      width: isScreenMobile() ? null : 250,
      editable: true,
      render: (text, record) => {
        return (
          <>
            {record.description} <StateTag state={record.state} />
          </>
        );
      },
    },
    ...columnsExtended,
    {
      title: "Yaratma Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text, record) => {
        return formatDate(record.createdAt);
      },
    },
    mode === DISPLAY_MODE.EDIT
      ? {
          title: "İşlemler",
          width: 150,
          dataIndex: "operation",
          fixed: isScreenMobile() ? null : "right",
          render: (text, record) => {
            return isEditing(record) ? (
              <span>
                <Tooltip title="Sakla">
                  <Button
                    htmlType="submit"
                    type="primary"
                    loading={
                      store.process === ACTION_TYPES.CREATING_DATA ||
                      store.process === ACTION_TYPES.UPDATING_DATA
                    }
                    icon={<SaveOutlined />}
                    style={{
                      marginRight: 8,
                    }}
                  />
                </Tooltip>
                <Popconfirm
                  title="Emin Misin?"
                  onConfirm={() => handleCancel(record.id)}
                  icon={<RollbackOutlined />}
                  okText="İptal"
                  cancelText="Geri"
                >
                  <Tooltip title="İptal" placement="bottom">
                    <Button
                      type="default"
                      icon={<RollbackOutlined />}
                      style={{}}
                    />
                  </Tooltip>
                </Popconfirm>
              </span>
            ) : (
              <span>
                <EditParameterButton
                  authSubject={authSubject}
                  onEdit={(e) => {
                    e.preventDefault();
                    setSelectedParameter(record);
                  }}
                  disabled={
                    selectedParameter || record.state !== RECORD_STATE.ACTIVE
                      ? true
                      : false
                  }
                />
                <DeleteParameterButton
                  authSubject={authSubject}
                  mode={record.state !== "ACTIVE" ? "restore" : "delete"}
                  onRestore={() =>
                    actions.restore({
                      id: record.id,
                      version: record.version,
                    })
                  }
                  onDelete={() => handleDelete(record)}
                  disabled={selectedParameter ? true : record.id === "new"}
                  loading={store.process === ACTION_TYPES.REMOVING_DATA}
                />

                {ItemsComponent ? (
                  <Tooltip title="Detaylar">
                    <Button
                      icon={<UnorderedListOutlined />}
                      onClick={() => {
                        setSelectedParameter(record);
                        setDrawerVisibility(true);
                      }}
                    />
                  </Tooltip>
                ) : null}
              </span>
            );
          },
        }
      : {},
  ];

  const form = useForm({
    mode: "onSubmit",
    resolver: yupResolver(FormValidation),
    defaultValues: {},
  });

  const { handleSubmit, errors, reset } = form;

  const formOnSubmit = async (form) => {
    clean(form);
    if (selectedParameter?.id) {
      let _selectedParameter = { ...selectedParameter };
      delete _selectedParameter.parentInterestConditionType;
      delete _selectedParameter.rule;
      await update({ ..._selectedParameter, ...form });
    } else {
      await create(form);
    }

    handleCancel();
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0)
      message.error("Girdiğiniz değerleri kontrol ediniz.");
  }, [errors]);

  const handleDelete = async (record) => {
    await remove({ id: record.id, version: record.version });
    handleCancel();
  };

  const handleCancel = () => {
    if (!selectedParameter) {
      setTableData((oldArray) => oldArray.filter((item) => item.id));
    }

    setSelectedParameter(null);
  };

  const handleAdd = () => {
    setCurrentPage(1);
    reset();
    const newData = {
      description: "",
      code: "",
      createdAt: moment(),
    };
    setTableData((oldArray) => [newData, ...oldArray]);
    setSelectedParameter(newData);
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputNode: col.inputNode,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        form: form,
      }),
    };
  });

  return (
    <>
      <form onSubmit={handleSubmit(formOnSubmit)}>
        <Table
          rowKey={(row) => row.id}
          scroll={{ x: 1000, scrollToFirstRowOnChange: true }}
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          locale={{
            emptyText: IsAuthorized
              ? "Kayıt Bulunamadı"
              : "Bu işlem için yetkiniz yok. Sistem yöneticiniz ile iletişime geçebilirsiniz.",
          }}
          title={
            mode === DISPLAY_MODE.EDIT
              ? () => {
                  return (
                    <>
                      <NewParameterButton
                        disabled={selectedParameter ? true : false}
                        onAdd={() => handleAdd()}
                        authSubject={authSubject}
                      />
                      <ArchiveSwitch
                        key="archive"
                        style={{ float: "right" }}
                        authSubject={authSubject}
                        onChange={(checked) => actions.setShowArchived(checked)}
                      />
                    </>
                  );
                }
              : null
          }
          bordered
          dataSource={tableData}
          columns={mergedColumns}
          size="small"
          pagination={false}
          loading={store.process === ACTION_TYPES.FETCHING_DATA}
          rowClassName="editable-row"
        />
      </form>

      {ItemsComponent ? (
        <Drawer
          width={"50%"}
          placement="right"
          headerStyle={{ position: "absolute", width: "100%" }}
          bodyStyle={{ overflowY: "auto", height: "100%" }}
          title={selectedParameter?.description}
          destroyOnClose={true}
          visible={drawerVisibility}
          onClose={() => {
            setDrawerVisibility(false);
            setSelectedParameter(null);
          }}
        >
          <div style={{ marginTop: 46 }}>
            <ItemsComponent
              parentId={selectedParameter?.id}
              title={selectedParameter?.description}
              api={api}
              onRefreshList={() => {
                actions.fetch();
              }}
            />
          </div>
        </Drawer>
      ) : null}
    </>
  );
}
